import { buildHwApiUrl } from "@hunkware-ui/common";

import { HW_API_URL } from "../../configs";
import { fetcher } from "../../libs";

import {
  AccountCreateParams,
  AccountCreateResponse,
  AccountUpdateParams,
  AccountUpdateResponse,
  AccountPingParams,
  AccountPingResponse,
  CallRequestParams,
  CallRequestResponse,
  MarkLeadInProgressParams,
  MarkLeadInProgressResponse,
  LeadMetaUpsertParams,
  LeadMetaUpsertResponse,
  LeadUpdateParams,
  LeadUpdateResponse,
  MarkLeadLostParams,
  MarkLeadLostResponse,
  SendDispatchMessageParams,
  SendDispatchMessageResponse,
} from "./types";

/**
 * Create-or-find an account and mint its customer token.
 *
 * **Transport-only migration.** The payload and every response field are identical on both hosts —
 * the Node endpoint reproduces the legacy snake_case body and the `meta` envelope exactly (see
 * `docs/migrations/website-account-token-generate/`).
 *
 * The Node URL is **absolute**, which makes axios ignore the client's `baseURL`
 * (`buildFullPath` returns the absolute value verbatim) — so one `fetcher` addresses both hosts and
 * BOTH interceptors still run: the request is snake_cased on the way out and camelCased on the way
 * back, which is exactly what keeps the existing camelCase response types working unchanged.
 *
 * Built through `buildHwApiUrl` rather than concatenated: `VITE_HW_API_URL` is bare `/api` in every
 * booking-v4 environment today, but the helper strips from `/api` onward so the route stays correct
 * if that value is ever scoped the way booking-v2's is.
 */
export const accountCreate = ({
  locationId,
  payload,
  useNode = false,
}: AccountCreateParams) =>
  fetcher.post<AccountCreateResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/tokens/generate`,
        )
      : `/locations/${locationId}/accounts/tokens/generate`,
    payload,
  );

export const accountUpdate = ({
  locationId,
  accountId,
  token,
  payload,
}: AccountUpdateParams) =>
  fetcher.put<AccountUpdateResponse>(
    `/locations/${locationId}/accounts/${accountId}`,
    payload,
    {
      params: { token },
    },
  );

/**
 * Ask the Sales Lead Center to call the customer back.
 *
 * **Transport-only migration** (`docs/migrations/call-requests/`). Same body, same `?token=` gate,
 * and the Node endpoint reproduces the legacy snake_case body and `meta` envelope exactly. The one
 * caller discards the response, so only resolve-vs-reject and the error interceptor's `meta` reads
 * matter — identical on both hosts. Absolute Node URL for the reason `accountCreate` documents.
 */
export const createCallRequest = ({
  locationId,
  accountId,
  token,
  payload,
  useNode = false,
}: CallRequestParams) =>
  fetcher.post<CallRequestResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/calls/requests`,
        )
      : `/locations/${locationId}/accounts/${accountId}/calls/requests`,
    payload,
    {
      params: { token },
    },
  );

/**
 * Update the lead the funnel created — its target date, category, task text or promo code.
 *
 * **Transport-only migration** (`docs/migrations/website-account-lead-update/`). Same body, same `?token=` gate, and
 * the Node endpoint reproduces the legacy snake_case body and `meta` envelope exactly. Both callers discard the
 * response, so only resolve-vs-reject and the error interceptor's `meta` reads matter — identical on both hosts.
 * Absolute Node URL for the reason `accountCreate` documents.
 */
export const leadUpdate = ({
  locationId,
  accountId,
  leadId,
  token,
  payload,
  useNode = false,
}: LeadUpdateParams) =>
  fetcher.put<LeadUpdateResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/leads/${leadId}`,
        )
      : `/locations/${locationId}/accounts/${accountId}/leads/${leadId}`,
    payload,
    {
      params: { token },
    },
  );

/**
 * hunkware-api only — this endpoint has no php-api counterpart, so unlike the migrated routes
 * there is no transport flag and nothing to fall back to. The absolute URL bypasses the
 * fetcher's `baseURL` (php-api's `/public/website`) while keeping its casing transforms and
 * error handling.
 */
export const markLeadInProgress = ({
  locationId,
  accountId,
  leadId,
  token,
}: MarkLeadInProgressParams) =>
  fetcher.post<MarkLeadInProgressResponse>(
    buildHwApiUrl(
      HW_API_URL,
      `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/leads/${leadId}/in-progress`,
    ),
    {},
    {
      params: { token },
    },
  );

/**
 * hunkware-api only (CID-7928), like `markLeadInProgress`: no php-api counterpart, so no transport
 * flag. Analytics-only — callers must not let a failure here stop the customer.
 */
export const leadMetaUpsert = ({
  locationId,
  accountId,
  leadId,
  token,
  payload,
}: LeadMetaUpsertParams) =>
  fetcher.patch<LeadMetaUpsertResponse>(
    buildHwApiUrl(
      HW_API_URL,
      `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/leads/${leadId}/meta`,
    ),
    payload,
    {
      params: { token },
    },
  );

/**
 * The online-booking heartbeat: `booking_complete: false` while the customer fills the form,
 * `true` once the booking is placed.
 *
 * **Transport-only migration** (`docs/migrations/website-account-ping/`). Same body, same `?token=`
 * gate, and the Node endpoint reproduces the legacy snake_case body and `meta` envelope exactly —
 * every call site discards the response, so only resolve-vs-reject and the error interceptor's
 * `meta` reads matter, identical on both hosts. Absolute Node URL for the reason `accountCreate`
 * documents.
 *
 * @param useNode value of `HunkWare_Account_Public_Website_Actions_Node_Migration` — this route's own flag (owner decision:
 *   the booking-wizard ACTIONS ramp separately from the find/PUT epic flag). Roll back by turning it off; no deploy.
 */
export const accountPing = ({
  locationId,
  accountId,
  token,
  payload,
  useNode = false,
}: AccountPingParams) =>
  fetcher.post<AccountPingResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/ping`,
        )
      : `/locations/${locationId}/accounts/${accountId}/ping`,
    payload,
    {
      params: { token },
    },
  );

/**
 * The lost-reason create on hunkware-api (docs/migrations/account-lost-post/, CID-7764).
 *
 * Transport-only: same method, same body, same `?token=` gate, same reads. This caller discards the
 * response entirely — `markLeadLostExecutor` awaits and ignores it — so the only shape that matters
 * on either branch is the ERROR envelope, which the Node port reproduces exactly (doc 05) and which
 * this app's response interceptor reads for `meta.status.name` and `meta.errors[].http_code`.
 *
 * The Node URL is absolute, so axios ignores the client's php-api `baseURL` while BOTH interceptors
 * still run: the body is snake_cased on the way out, the response camelCased on the way back.
 *
 * @param useNode value of `HunkWare_Account_Public_Website_Node_Migration` — the public-website
 *   epic's one flag, the same switch `customer-portal` uses. Roll back by turning it off; no deploy.
 */
export const markLeadLost = ({
  locationId,
  accountId,
  token,
  payload,
  useNode = false,
}: MarkLeadLostParams) =>
  fetcher.post<MarkLeadLostResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/lost`,
        )
      : `/locations/${locationId}/accounts/${accountId}/lost`,
    payload,
    {
      params: { token },
    },
  );

/**
 * Account-scoped dispatch message — same endpoint/pattern customer-portal
 * uses when a client accepts a quote with no available times, or tries to
 * accept a same-day quote. Needs only account_id (no appointment/job
 * required), unlike the appointment-scoped dispatch message endpoint.
 *
 * **Transport-only migration** (`docs/migrations/public-account-dispatch-message/`), the
 * `accountCreate` shape: the Node URL is absolute, so axios ignores `baseURL` while both interceptors
 * still run, and `?token=` rides as the same `params` on both hosts — it is the route's whole
 * credential. Nothing reads the response; the error path reads only the legacy `meta`, which the Node
 * route reproduces.
 */
export const sendDispatchMessage = ({
  locationId,
  accountId,
  token,
  payload,
  useNode = false,
}: SendDispatchMessageParams) =>
  fetcher.post<SendDispatchMessageResponse>(
    useNode
      ? buildHwApiUrl(
          HW_API_URL,
          `/api/v1/public/website/locations/${locationId}/accounts/${accountId}/dispatchmessage`,
        )
      : `/locations/${locationId}/accounts/${accountId}/dispatchmessage`,
    payload,
    {
      params: { token },
    },
  );
